import { RocketIcon, TagIcon } from 'assets/icons';
import { Button } from 'Components';
import { BREAKPOINTS, CURRENCIES_SYMBOL, PERIOD_MONTH, PERIOD_YEAR } from 'constants/common';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import styled from 'styled-components';
import { Spinner } from 'v6/Components/Spinner';
import { ChargeBeeEstimate } from './types';

const formatPrice = (amount: number) => {
  return (amount / 100).toFixed(2);
};

export const CURRENCIES_PREFIX: Record<string, string> = {
  usd: 'US$',
  aud: 'AU$',
  eur: '€',
  gbp: '£',
};

const OrderSummary = ({
  isLoading,
  estimate,
  selectedPlanId,
  isSubscribed,
  isSubmittingForm,
  isFormValid,
  handleSubmit,
}: {
  isLoading: boolean;
  estimate: ChargeBeeEstimate['next_invoice_estimate'] | null;
  selectedPlanId: string;
  isSubscribed: boolean;
  isSubmittingForm: boolean;
  isFormValid: boolean;
  handleSubmit: () => {};
}) => {
  // const defaultPeriod =
  //   selectedPlanId && !selectedPlanId.includes("-annual") ? PERIOD_MONTH : PERIOD_YEAR;
  const nextPaymentDate = dayjs().add(7, 'd').format('D MMM YYYY');

  const period = useMemo(
    () =>
      selectedPlanId && !(selectedPlanId.includes('yearly') || selectedPlanId.includes('annual'))
        ? PERIOD_MONTH
        : PERIOD_YEAR,
    [selectedPlanId],
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!estimate) {
    return <></>;
  }

  const selectedCurrency = estimate.currency_code?.toLowerCase();
  const selectedCurrencySymbol = CURRENCIES_SYMBOL[selectedCurrency];
  const baseItem = estimate.line_items.find(i => i.entity_type === 'plan_item_price');
  const extraItems = estimate.line_items.find(i => i.entity_type === 'addon_item_price');
  if (!baseItem) {
    return <p>An error occurred rendering your estimate, please contact support for assistance.</p>;
  }
  const basePrice = baseItem.amount || 0;
  const extraPrice = extraItems?.amount || 0;
  const totalPrice = estimate.total || 0;
  const hasTax = estimate.taxes.length > 0;
  const taxPrice = hasTax ? estimate.taxes?.[0]?.amount : 0;
  const hasDiscount = estimate.line_item_discounts.length > 0;
  const discount = hasDiscount ? estimate.line_item_discounts[0] : null;

  return selectedPlanId ? (
    <OrderSummaryWrapper data-testid="orderSummary">
      <Title>{!isSubscribed ? 'Order Summary' : 'Order Confirmation'}</Title>
      <Wrapper>
        <Row>
          <Text>Plan</Text>
          <Text bold color={'var(--Primary-P500, #4f31ff)'}>
            {baseItem.description.replace('Sked ', '')}
          </Text>
        </Row>
        <Row>
          <Text>Billing cycle</Text>
          <Text>{period === PERIOD_MONTH ? 'Monthly' : 'Annual'}</Text>
        </Row>
        <Divider />
        <Row>
          <Text>Plan price</Text>
          <Text>
            {selectedCurrencySymbol}
            {formatPrice(basePrice)}
          </Text>
        </Row>
        {extraItems && extraItems?.quantity > 0 && (
          <Row>
            <Text>Extra accounts</Text>
            <Text>
              {extraItems.quantity}x {selectedCurrencySymbol}
              {/* show the price per addon here */}
              {formatPrice(extraPrice / extraItems.quantity)}
            </Text>
          </Row>
        )}
        {hasTax && (
          <Row>
            <Text>GST (10%)</Text>
            <Text>
              {selectedCurrencySymbol}
              {formatPrice(taxPrice)}
            </Text>
          </Row>
        )}
        {hasDiscount && discount && (
          <Row>
            <Text color="var(--Green-G500, #10B981)">
              <CouponWrap>
                <TagIcon />
                {discount.coupon_id}
              </CouponWrap>
            </Text>
            <Text color="var(--Green-G500, #10B981)">
              {`- ${selectedCurrencySymbol}
              ${formatPrice(discount.discount_amount)}`}
            </Text>
          </Row>
        )}
        <Divider />
        <Row>
          <Text size="lg" bold>
            Total
          </Text>
          <Text size="lg" bold>
            {CURRENCIES_PREFIX[selectedCurrency] || '$'}
            {formatPrice(totalPrice)}/{period}
          </Text>
        </Row>
        <Divider />
        <Row>
          <Text size="lg" bold>
            Due Today
          </Text>
          <Text size="lg" bold color="var(--Green-G500, #10B981)">
            {CURRENCIES_PREFIX[selectedCurrency] || '$'}0.00
          </Text>
        </Row>
        <StyledButton
          data-testid="confirmPaymentButton"
          disabled={!isFormValid || isSubmittingForm}
          onClick={handleSubmit}
          title={!isFormValid ? 'Please fill all fields to continue.' : 'Please wait...'}
        >
          {isSubmittingForm ? (
            'Please wait...'
          ) : (
            <div>
              Confirm Order & Start FREE Trial
              <SubmitRocketIcon>
                <RocketIcon />
              </SubmitRocketIcon>
            </div>
          )}
        </StyledButton>
        <RowWithBigMargin>
          <Text>
            Your card will be charged{' '}
            <Bold>
              {CURRENCIES_PREFIX[selectedCurrency] || '$'}
              {formatPrice(totalPrice)}
              {hasTax ? ` (including GST)` : ``}
            </Bold>{' '}
            on <Bold>{nextPaymentDate} </Bold>at the end of the 7 day free trial.
          </Text>
        </RowWithBigMargin>
      </Wrapper>
    </OrderSummaryWrapper>
  ) : (
    <></>
  );
};

const OrderSummaryWrapper = styled.div`
  width: 400px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--Brand-Vibrant-Blue, #4f31ff);

  @media (max-width: ${BREAKPOINTS.tablet}px) {
    width: 100%;
  }
`;

const Title = styled.div`
  color: var(--Primary-P500, #4f31ff);

  /* Web App/Heading 3 */
  font-family: Gotham SSm A, Gotham SSm B, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px; /* 133.333% */
  text-align: center;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

const Bold = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  color: var(--black);
`;

const Text = styled.div<{ size?: 'lg'; bold?: boolean; color?: string }>`
  color: ${props => (props.color ? props.color : 'var(--Text-T01, #333)')};
  font-family: Inter, sans-serif;
  font-size: ${props => (props.size === 'lg' ? '16px' : '14px')};
  font-style: normal;
  font-weight: ${props => (props.bold ? 600 : 400)};
  line-height: 20px;
`;

const Wrapper = styled.div`
  margin-top: 24px;
`;

const RowWithBigMargin = styled(Row)`
  margin-top: 16px;
`;

const Divider = styled.div`
  border: 1px solid #e6e6e6;
  margin: 13px 0;
`;

const StyledButton = styled(Button)`
  margin-top: 17px;
`;

const SubmitRocketIcon = styled.div`
  width: 20px;
  height: 20px;
  display: inline-block;
  position: relative;
  bottom: -5px;
  margin-left: 5px;
`;

const CouponWrap = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-right: 4px;
  }
`;

export default OrderSummary;
